import React, { useState, useEffect } from 'react';
import { Layout, theme, Breadcrumb, Row, Col, Button } from 'antd';
import { useLocation, Link } from 'react-router-dom';
import SideBar from '../components/Sidebar';
import HeaderUserSection from '../components/HeaderUserSection';
import ConsoleOverlay from '../components/ConsoleOverlay';
import { useKeycloak } from '@react-keycloak/web';
import { getUserInfo } from '../services/userService';
import { CodeOutlined } from '@ant-design/icons';
import { useAppTheme } from '../hooks/useAppTheme';
import './MainLayout.css';

const { Content, Footer, Sider } = Layout;

const breadcrumbNameMap = {
    '/home': 'Dashboard',
    '/preferences': 'Preferences',
    '/history': 'History',
    '/console': 'Console',
    '/account': 'Account',
    '/user-guides': 'User Guide',
    '/workspace': 'Workspace',
    '/workspace/marketplace': 'AI/ML Marketplace',
    '/workspace/dataset-search': 'Dataset Search',
    '/workspace/workflow-creator': 'Workflow Creator',
    '/workspace/workflow-editor': 'Workflow Editor',
    '/workspace/data-visualization': 'Data Visualization',
    '/workspace/map-visualization': 'Map Visualization',
    '/workspace/file-explorer': 'File Explorer',
    '/workspace/xr-vr': 'XR/VR',
};

const MainLayout = (props) => {

    theme.useToken();
    const { isLight } = useAppTheme();

    const [collapsed, setCollapsed] = useState(false);
    const [consoleOpen, setConsoleOpen] = useState(false);

    const location = useLocation();
    const isHomeRoute = location.pathname === '/' || location.pathname === '/home';
    const pathSnippets = location.pathname.split('/').filter((i) => i);
    const extraBreadcrumbItems = pathSnippets.map((_, index) => {
        const url = `/${pathSnippets.slice(0, index + 1).join('/')}`;
        return {
            key: url,
            title: <Link to={url}>{breadcrumbNameMap[url]}</Link>,
        };
    });
    const breadcrumbItems = [
        {
            title: (
                <Link to='/home'>
                    <span className='breadcrumb-home'>Home</span>
                </Link>
            ),
            key: 'home',
        },
    ].concat(extraBreadcrumbItems);

    const { keycloak } = useKeycloak();
    const [userinfo, setUserinfo] = useState({})

    useEffect(() => {
        if (!keycloak?.token) return;

        getUserInfo(keycloak.token)
            .then((response) => setUserinfo(response))
            .catch(() => setUserinfo({}));
    }, [keycloak?.token]);

    useEffect(() => {
        document.body.style.overflow = '';
    }, [location.pathname]);


    return (
        <Layout
            style={{ minHeight: '100vh' }}>

            <Sider
                collapsible
                collapsed={collapsed}
                onCollapse={(value) => setCollapsed(value)}
                theme={isLight ? 'light' : 'dark'}
                width={250}
                collapsedWidth={64}
                trigger={null}
            >
                <SideBar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
            </Sider>
            <Layout className="site-layout">
                <Content style={{ margin: '0 16px' }}>
                    <div className={`site-layout__header ${isHomeRoute ? 'site-layout__header--home' : ''}`}>
                        <Row justify='space-between' align='middle' gutter={16} className='site-layout__header-row'>
                            <Col flex='auto' className='site-layout__header-breadcrumb'>
                                <Breadcrumb items={breadcrumbItems} separator='>' />
                            </Col>
                            <Col flex='none' className='site-layout__header-actions-col'>
                                <div className='site-layout__header-actions'>
                                    <Button
                                        type='text'
                                        className={`site-layout__console-btn ${consoleOpen ? 'site-layout__console-btn--active' : ''}`}
                                        icon={<CodeOutlined />}
                                        onClick={() => setConsoleOpen((v) => !v)}
                                    >
                                        Console
                                    </Button>
                                    <HeaderUserSection
                                        user={{
                                            username:
                                                userinfo.preferred_username ||
                                                keycloak?.tokenParsed?.preferred_username ||
                                                '',
                                            firstName:
                                                userinfo.given_name ||
                                                keycloak?.tokenParsed?.given_name ||
                                                '',
                                            lastName:
                                                userinfo.family_name ||
                                                keycloak?.tokenParsed?.family_name ||
                                                '',
                                            name: userinfo.name || keycloak?.tokenParsed?.name || '',
                                        }}
                                    />
                                </div>
                                <ConsoleOverlay open={consoleOpen} onClose={() => setConsoleOpen(false)} />
                            </Col>
                        </Row>
                    </div>

                    <main className={`site-layout__main ${isHomeRoute ? 'site-layout__main--home' : ''}`}>
                        {props.children}
                    </main>

                </Content>
                <Footer className='site-layout__footer'>
                    <div className='site-layout__footer-inner'>
                        <div className='site-layout__footer-left'>
                            <a className='site-layout__footer-link' href='https://www.eo4eu.eu' target='_blank' rel='noreferrer'>
                                Back to EO4EU
                            </a>
                            <span className='site-layout__footer-sep' aria-hidden>
                                ·
                            </span>
                            <span>Copyright © EO4EU {new Date().getFullYear()}</span>
                        </div>
                        <div className='site-layout__footer-right'>
                            <img
                                className='site-layout__footer-eu-flag'
                                src='https://www.eo4eu.eu/themes/eo4eu/images/euflagwhite.svg'
                                alt='European Union flag'
                            />
                        </div>
                    </div>
                </Footer>
            </Layout>
        </Layout>

    );
}

export default MainLayout;
